import React, { useState, useEffect, useContext } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Box,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { AuthContext } from '../../../context/AuthContext';
import { obtenerDatosCuadranteParaValidacion } from '../data/FirestoreCuadrantes';
import './CuadranteValidator.scss';

const CuadranteValidator = ({ cuadranteId, configuracion, onValidationComplete }) => {
  const { currentService } = useContext(AuthContext);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [cuadranteData, setCuadranteData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (cuadranteId && currentService) {
      cargarDatosCuadrante();
    }
  }, [cuadranteId, currentService]);

  const cargarDatosCuadrante = async () => {
    try {
      setError(null);
      
      if (!cuadranteId || cuadranteId === 'demo-cuadrante') {
        // Usar datos de ejemplo solo para demo
        const datosEjemplo = {
          id: cuadranteId,
          turnos: [
            {
              id: '1',
              vigilante: 'Juan Pérez',
              vigilanteId: 'vp1',
              fecha: '2024-01-15',
              inicioTurno: '08:00',
              finTurno: '20:00', // 12 horas - excede el máximo
              servicio: 'Centro Comercial A',
              horasContrato: 160,
              horasMes: 185, // Excede las horas máximas
              horasExtra: 25,
              ubicacionServicio: 'Madrid Centro',
              ubicacionVigilante: 'Madrid Norte',
              distancia: 35, // Excede la distancia máxima
              salario: 1200,
              disponible: true
            },
            {
              id: '2',
              vigilante: 'María García',
              vigilanteId: 'vp2',
              fecha: '2024-01-15',
              inicioTurno: '16:00',
              finTurno: '00:00',
              servicio: 'Centro Comercial A',
              horasContrato: 160,
              horasMes: 175, // Aproximándose al límite
              horasExtra: 15,
              ubicacionServicio: 'Madrid Centro',
              ubicacionVigilante: 'Madrid Sur',
              distancia: 8,
              salario: 1100,
              disponible: true
            },
            {
              id: '3',
              vigilante: 'Carlos López',
              vigilanteId: 'vp3',
              fecha: '2024-01-15',
              inicioTurno: '00:00',
              finTurno: '08:00',
              servicio: 'Centro Comercial A',
              horasContrato: 160,
              horasMes: 170,
              horasExtra: 10,
              ubicacionServicio: 'Madrid Centro',
              ubicacionVigilante: 'Madrid Este',
              distancia: 12,
              salario: 1150,
              disponible: false // No disponible
            },
            {
              id: '4',
              vigilante: 'Ana Martínez',
              vigilanteId: 'vp4',
              fecha: '2024-01-15',
              inicioTurno: '08:00',
              finTurno: '16:00',
              servicio: 'Centro Comercial B',
              horasContrato: 160,
              horasMes: 160,
              horasExtra: 0,
              ubicacionServicio: 'Madrid Oeste',
              ubicacionVigilante: 'Madrid Centro',
              distancia: 28, // Aproximándose al límite
              salario: 1050,
              disponible: true
            }
          ]
        };
        setCuadranteData(datosEjemplo);
      } else {
        // Cargar datos reales del cuadrante
        const datosReales = await obtenerDatosCuadranteParaValidacion(cuadranteId, currentService);
        setCuadranteData(datosReales);
      }
    } catch (error) {
      console.error('Error al cargar datos del cuadrante:', error);
      setError('Error al cargar los datos del cuadrante. Verifica que el cuadrante existe y tienes permisos para acceder.');
    }
  };

  const validarCuadrante = async () => {
    setIsValidating(true);
    
    try {
      // Simular tiempo de validación
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Usar configuración por defecto si no hay una seleccionada
      const configToUse = configuracion || {
        nombre: 'Configuración Estándar',
        horasMesConvenio: 160,
        horasExtraMaximas: 20,
        horasMaximasTurno: 12,
        maxKmCambioServicio: 25,
        dietaPorKm: 0.25,
        dietaMinima: 15
      };
      
      const resultados = await ejecutarValidaciones(cuadranteData, configToUse);
      setValidationResults(resultados);
      
      if (onValidationComplete) {
        onValidationComplete(resultados);
      }
    } catch (error) {
      console.error('Error en la validación:', error);
    } finally {
      setIsValidating(false);
    }
  };

  const ejecutarValidaciones = async (datos, config) => {
    const errores = [];
    const advertencias = [];
    const resumen = {
      totalTurnos: datos.turnos.length,
      turnosValidos: 0,
      turnosConErrores: 0,
      turnosConAdvertencias: 0
    };

    // Validar cada turno
    for (const turno of datos.turnos) {
      const turnoErrores = [];
      const turnoAdvertencias = [];

      // 1. Validar horas del mes
      if (turno.horasMes > config.horasMesConvenio + config.horasExtraMaximas) {
        turnoErrores.push(`Excede las horas máximas del mes (${turno.horasMes}h vs ${config.horasMesConvenio + config.horasExtraMaximas}h permitidas)`);
      } else if (turno.horasMes > config.horasMesConvenio) {
        turnoAdvertencias.push(`Aproximándose al límite de horas extra (${turno.horasMes - config.horasMesConvenio}h extra)`);
      }

      // 2. Validar duración del turno
      const duracionTurno = calcularDuracionTurno(turno.inicioTurno, turno.finTurno);
      if (duracionTurno > config.horasMaximasTurno) {
        turnoErrores.push(`Turno excede las horas máximas (${duracionTurno}h vs ${config.horasMaximasTurno}h permitidas)`);
      }

      // 3. Validar distancia de transporte
      if (turno.distancia > config.maxKmCambioServicio) {
        const dietaCalculada = Math.max(config.dietaMinima, turno.distancia * config.dietaPorKm);
        turnoAdvertencias.push(`Servicio a ${turno.distancia}km (máximo ${config.maxKmCambioServicio}km). Dieta requerida: ${dietaCalculada.toFixed(2)}€`);
      }

      // 4. Validar disponibilidad
      if (!turno.disponible) {
        turnoErrores.push('Vigilante no disponible (vacaciones, baja, etc.)');
      }

      if (turnoErrores.length > 0) {
        errores.push({
          turnoId: turno.id,
          vigilante: turno.vigilante,
          errores: turnoErrores
        });
        resumen.turnosConErrores++;
      } else {
        resumen.turnosValidos++;
      }

      if (turnoAdvertencias.length > 0) {
        advertencias.push({
          turnoId: turno.id,
          vigilante: turno.vigilante,
          advertencias: turnoAdvertencias
        });
        resumen.turnosConAdvertencias++;
      }
    }

    // Validaciones globales del cuadrante
    const validacionesGlobales = await validarRestriccionesGlobales(datos, config);
    errores.push(...validacionesGlobales.errores);
    advertencias.push(...validacionesGlobales.advertencias);

    return {
      esValido: errores.length === 0,
      errores,
      advertencias,
      resumen,
      configuracionUsada: config.nombre,
      fechaValidacion: new Date().toISOString()
    };
  };

  const validarRestriccionesGlobales = async (datos, config) => {
    const errores = [];
    const advertencias = [];

    // Agrupar turnos por vigilante
    const turnosPorVigilante = {};
    datos.turnos.forEach(turno => {
      if (!turnosPorVigilante[turno.vigilanteId]) {
        turnosPorVigilante[turno.vigilanteId] = [];
      }
      turnosPorVigilante[turno.vigilanteId].push(turno);
    });

    // Validar cada vigilante
    for (const [vigilanteId, turnos] of Object.entries(turnosPorVigilante)) {
      const vigilante = turnos[0]; // Usar el primer turno para obtener datos del vigilante
      
      // Ordenar turnos por fecha
      turnos.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

      // 1. Validar descansos entre turnos consecutivos
      for (let i = 0; i < turnos.length - 1; i++) {
        const turnoActual = turnos[i];
        const turnoSiguiente = turnos[i + 1];
        
        const descanso = calcularDescansoEntreTurnos(turnoActual, turnoSiguiente);
        
        if (descanso < config.descansoMinimoEntreTurnos) {
          errores.push({
            turnoId: turnoSiguiente.id,
            vigilante: vigilante.vigilante,
            errores: [`Descanso insuficiente entre turnos: ${descanso}h (mínimo ${config.descansoMinimoEntreTurnos}h)`]
          });
        }
      }

      // 2. Validar jornadas nocturnas consecutivas
      const jornadasNocturnas = contarJornadasNocturnasConsecutivas(turnos);
      if (jornadasNocturnas > config.maxJornadasNocturnasConsecutivas) {
        advertencias.push({
          turnoId: turnos[jornadasNocturnas - 1].id,
          vigilante: vigilante.vigilante,
          advertencias: [`${jornadasNocturnas} jornadas nocturnas consecutivas (máximo ${config.maxJornadasNocturnasConsecutivas})`]
        });
      }

      // 3. Validar descansos en fin de semana
      const descansosFinSemana = contarDescansosFinSemana(turnos);
      if (descansosFinSemana < config.descansosFinSemanaMes) {
        advertencias.push({
          turnoId: turnos[0].id,
          vigilante: vigilante.vigilante,
          advertencias: [`Solo ${descansosFinSemana} descansos en fin de semana este mes (mínimo ${config.descansosFinSemanaMes})`]
        });
      }

      // 4. Validar días de vacaciones y permisos
      const diasTrabajados = turnos.length;
      const diasVacaciones = vigilante.vacaciones?.length || 0;
      const diasPermisos = vigilante.permisos?.length || 0;
      
      if (diasVacaciones < config.diasVacaciones / 12) { // Proporción mensual
        advertencias.push({
          turnoId: turnos[0].id,
          vigilante: vigilante.vigilante,
          advertencias: [`Solo ${diasVacaciones} días de vacaciones tomados (mínimo ${Math.ceil(config.diasVacaciones / 12)} por mes)`]
        });
      }

      // 5. Validar turnos largos y descansos posteriores
      for (let i = 0; i < turnos.length; i++) {
        const turno = turnos[i];
        const duracionTurno = calcularDuracionTurno(turno.inicioTurno, turno.finTurno);
        
        if (duracionTurno >= config.descansoTrasTurnosLargos.turnoMinimo) {
          // Verificar si hay descanso suficiente después
          if (i < turnos.length - 1) {
            const turnoSiguiente = turnos[i + 1];
            const descanso = calcularDescansoEntreTurnos(turno, turnoSiguiente);
            
            if (descanso < config.descansoTrasTurnosLargos.horas) {
              errores.push({
                turnoId: turnoSiguiente.id,
                vigilante: vigilante.vigilante,
                errores: [`Turno largo de ${duracionTurno}h requiere mínimo ${config.descansoTrasTurnosLargos.horas}h de descanso (solo ${descanso}h)`]
              });
            }
          }
        }
      }

      // 6. Validar distribución de horas por semana
      const horasPorSemana = calcularHorasPorSemana(turnos);
      for (const [semana, horas] of Object.entries(horasPorSemana)) {
        const horasMaximasSemana = config.horasMesConvenio / 4; // Aproximación semanal
        if (horas > horasMaximasSemana + config.horasExtraMaximas / 4) {
          advertencias.push({
            turnoId: turnos[0].id,
            vigilante: vigilante.vigilante,
            advertencias: [`Semana ${semana}: ${horas}h trabajadas (máximo recomendado ${horasMaximasSemana + config.horasExtraMaximas / 4}h)`]
          });
        }
      }
    }

    return { errores, advertencias };
  };

  // Funciones auxiliares para validaciones globales
  const calcularDescansoEntreTurnos = (turno1, turno2) => {
    const fecha1 = new Date(`${turno1.fecha}T${turno1.finTurno}:00`);
    const fecha2 = new Date(`${turno2.fecha}T${turno2.inicioTurno}:00`);
    
    // Si el segundo turno es al día siguiente
    if (fecha2 < fecha1) {
      fecha2.setDate(fecha2.getDate() + 1);
    }
    
    const diferenciaMs = fecha2.getTime() - fecha1.getTime();
    return diferenciaMs / (1000 * 60 * 60); // Convertir a horas
  };

  const contarJornadasNocturnasConsecutivas = (turnos) => {
    let maxConsecutivas = 0;
    let consecutivasActuales = 0;
    
    for (const turno of turnos) {
      const horaInicio = parseInt(turno.inicioTurno.split(':')[0]);
      const horaFin = parseInt(turno.finTurno.split(':')[0]);
      
      // Considerar turno nocturno si empieza después de las 22h o termina antes de las 6h
      const esNocturno = horaInicio >= 22 || horaFin <= 6 || 
                        (horaInicio < horaFin && horaInicio >= 22) ||
                        (horaFin < horaInicio && horaFin <= 6);
      
      if (esNocturno) {
        consecutivasActuales++;
        maxConsecutivas = Math.max(maxConsecutivas, consecutivasActuales);
      } else {
        consecutivasActuales = 0;
      }
    }
    
    return maxConsecutivas;
  };

  const contarDescansosFinSemana = (turnos) => {
    const semanas = new Set();
    let descansosFinSemana = 0;
    
    // Obtener todas las semanas del mes
    turnos.forEach(turno => {
      const fecha = new Date(turno.fecha);
      const semana = `${fecha.getFullYear()}-W${Math.ceil((fecha.getDate() + fecha.getDay()) / 7)}`;
      semanas.add(semana);
    });
    
    // Contar semanas donde no hay turnos en fin de semana
    for (const semana of semanas) {
      const turnosSemana = turnos.filter(turno => {
        const fecha = new Date(turno.fecha);
        const semanaTurno = `${fecha.getFullYear()}-W${Math.ceil((fecha.getDate() + fecha.getDay()) / 7)}`;
        return semanaTurno === semana;
      });
      
      const tieneTurnoFinSemana = turnosSemana.some(turno => {
        const fecha = new Date(turno.fecha);
        return fecha.getDay() === 0 || fecha.getDay() === 6; // Domingo o Sábado
      });
      
      if (!tieneTurnoFinSemana) {
        descansosFinSemana++;
      }
    }
    
    return descansosFinSemana;
  };

  const calcularHorasPorSemana = (turnos) => {
    const horasPorSemana = {};
    
    turnos.forEach(turno => {
      const fecha = new Date(turno.fecha);
      const semana = `${fecha.getFullYear()}-W${Math.ceil((fecha.getDate() + fecha.getDay()) / 7)}`;
      const duracion = calcularDuracionTurno(turno.inicioTurno, turno.finTurno);
      
      if (!horasPorSemana[semana]) {
        horasPorSemana[semana] = 0;
      }
      horasPorSemana[semana] += duracion;
    });
    
    return horasPorSemana;
  };

  const calcularDuracionTurno = (inicio, fin) => {
    const [horaInicio, minInicio] = inicio.split(':').map(Number);
    const [horaFin, minFin] = fin.split(':').map(Number);
    
    let duracion = (horaFin * 60 + minFin) - (horaInicio * 60 + minInicio);
    if (duracion < 0) duracion += 24 * 60; // Turno que cruza medianoche
    
    return duracion / 60; // Convertir a horas
  };

  const getSeverityIcon = (tipo) => {
    switch (tipo) {
      case 'error':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'success':
        return <CheckIcon color="success" />;
      default:
        return <CheckIcon />;
    }
  };

  const getSeverityColor = (tipo) => {
    switch (tipo) {
      case 'error':
        return '#dc3545';
      case 'warning':
        return '#ffc107';
      case 'success':
        return '#28a745';
      default:
        return '#6c757d';
    }
  };

  return (
    <div className="cuadrante-validator">
      <Card className="validator-card">
        <CardContent>
          <div className="validator-header">
            <Typography variant="h6" component="h2">
              Validación de Cuadrante
            </Typography>
            {configuracion && (
              <Chip 
                label={`Configuración: ${configuracion.nombre}`}
                color="primary"
                variant="outlined"
              />
            )}
            {!cuadranteId || cuadranteId === 'demo-cuadrante' ? (
              <Chip 
                label="Modo Demo"
                color="warning"
                variant="outlined"
              />
            ) : null}
          </div>

          {!validationResults && (
            <div className="validator-controls">
              {error && (
                <Alert severity="error" style={{ marginBottom: '16px' }}>
                  {error}
                </Alert>
              )}
              
              <Button
                variant="contained"
                color="primary"
                onClick={validarCuadrante}
                disabled={isValidating || !cuadranteData}
                startIcon={isValidating ? <CircularProgress size={20} /> : <CheckIcon />}
                fullWidth
              >
                {isValidating ? 'Validando...' : cuadranteId === 'demo-cuadrante' ? 'Validar Cuadrante de Ejemplo' : 'Validar Cuadrante'}
              </Button>
              
              {!configuracion && (
                <Alert severity="info" style={{ marginTop: '16px' }}>
                  Usando configuración estándar para la validación. Selecciona una configuración específica en el panel de configuración para personalizar las normas.
                </Alert>
              )}
              
              {cuadranteId === 'demo-cuadrante' && (
                <Alert severity="info" style={{ marginTop: '16px' }}>
                  <strong>Nota:</strong> Este es un cuadrante de ejemplo para demostrar la funcionalidad de validación. 
                  Los datos mostrados son simulados y contienen errores y advertencias intencionales.
                </Alert>
              )}
              
              {!cuadranteData && !error && (
                <Alert severity="warning" style={{ marginTop: '16px' }}>
                  Cargando datos del cuadrante...
                </Alert>
              )}
            </div>
          )}

          {validationResults && (
            <div className="validation-results">
              <Alert 
                severity={validationResults.esValido ? "success" : "error"}
                style={{ marginBottom: '24px' }}
              >
                <Typography variant="h6">
                  {validationResults.esValido 
                    ? "✅ Cuadrante Válido"
                    : "❌ Cuadrante Inválido"
                  }
                </Typography>
                <Typography variant="body2">
                  {validationResults.esValido 
                    ? "El cuadrante cumple todas las normas configuradas"
                    : "Se encontraron errores que deben corregirse antes de aplicar el cuadrante"
                  }
                </Typography>
              </Alert>

              {/* Resumen de validación */}
              <Card variant="outlined" style={{ marginBottom: '24px' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Resumen de Validación
                  </Typography>
                  <Box className="validation-summary">
                    <div className="summary-item">
                      <ScheduleIcon color="primary" />
                      <div>
                        <Typography variant="body2" color="textSecondary">Total Turnos</Typography>
                        <Typography variant="h6">{validationResults.resumen.totalTurnos}</Typography>
                      </div>
                    </div>
                    <div className="summary-item">
                      <CheckIcon color="success" />
                      <div>
                        <Typography variant="body2" color="textSecondary">Válidos</Typography>
                        <Typography variant="h6">{validationResults.resumen.turnosValidos}</Typography>
                      </div>
                    </div>
                    <div className="summary-item">
                      <ErrorIcon color="error" />
                      <div>
                        <Typography variant="body2" color="textSecondary">Con Errores</Typography>
                        <Typography variant="h6">{validationResults.resumen.turnosConErrores}</Typography>
                      </div>
                    </div>
                    <div className="summary-item">
                      <WarningIcon color="warning" />
                      <div>
                        <Typography variant="body2" color="textSecondary">Con Advertencias</Typography>
                        <Typography variant="h6">{validationResults.resumen.turnosConAdvertencias}</Typography>
                      </div>
                    </div>
                  </Box>
                </CardContent>
              </Card>

              {/* Errores */}
              {validationResults.errores.length > 0 && (
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" color="error">
                      Errores Críticos ({validationResults.errores.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {validationResults.errores.map((error, index) => (
                        <React.Fragment key={index}>
                          <ListItem>
                            <ListItemIcon>
                              <PersonIcon color="error" />
                            </ListItemIcon>
                            <ListItemText
                              primary={error.vigilante}
                              secondary={
                                <ul className="error-list">
                                  {error.errores.map((err, errIndex) => (
                                    <li key={errIndex}>{err}</li>
                                  ))}
                                </ul>
                              }
                            />
                          </ListItem>
                          {index < validationResults.errores.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Advertencias */}
              {validationResults.advertencias.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" color="warning">
                      Advertencias ({validationResults.advertencias.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {validationResults.advertencias.map((warning, index) => (
                        <React.Fragment key={index}>
                          <ListItem>
                            <ListItemIcon>
                              <LocationIcon color="warning" />
                            </ListItemIcon>
                            <ListItemText
                              primary={warning.vigilante}
                              secondary={
                                <ul className="warning-list">
                                  {warning.advertencias.map((adv, advIndex) => (
                                    <li key={advIndex}>{adv}</li>
                                  ))}
                                </ul>
                              }
                            />
                          </ListItem>
                          {index < validationResults.advertencias.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Información de la validación */}
              <Card variant="outlined" style={{ marginTop: '24px' }}>
                <CardContent>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Configuración utilizada:</strong> {validationResults.configuracionUsada}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Fecha de validación:</strong> {new Date(validationResults.fechaValidacion).toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>

              {/* Botón para nueva validación */}
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setValidationResults(null)}
                style={{ marginTop: '16px' }}
                fullWidth
              >
                Nueva Validación
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CuadranteValidator; 